import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { LogIn } from "lucide-react";
import "./os.css";

const OS_ROLES = ["admin", "manager", "agent", "telecaller"];

export default function OSLogin() {
  const { user, login, checking } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (!checking && user && OS_ROLES.includes(user.role)) {
    return <Navigate to="/os" replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      if (OS_ROLES.includes(res.user?.role)) {
        toast.success("Welcome to Our First Realty OS");
        nav("/os", { replace: true });
      } else {
        toast.error("This account doesn't have OS access");
      }
    } else {
      toast.error(res.error || "Login failed");
    }
  };

  return (
    <div className="os-app" data-testid="os-login-page">
      <div className="min-h-screen grid md:grid-cols-2">
        {/* Left brand panel */}
        <div className="hidden md:flex flex-col justify-between p-10" style={{background: "linear-gradient(180deg, #0B0C0E 0%, #14161A 60%, #0B0C0E 100%)"}}>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg flex items-center justify-center font-bold text-white" style={{background: "var(--os-accent)"}}>OS</div>
            <div>
              <div className="font-bold text-lg" style={{color: "var(--os-text)"}}>Our First Realty OS</div>
              <div className="text-[11px] uppercase tracking-[0.18em]" style={{color: "var(--os-text-muted)"}}>Revenue Operating System</div>
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] mb-4" style={{color: "var(--os-accent)"}}>Every lead. Every rupee.</div>
            <h1 className="font-bold text-4xl leading-tight tracking-tight max-w-md" style={{color: "var(--os-text)"}}>
              No lead should go unanswered for more than 5 minutes.
            </h1>
            <p className="mt-5 max-w-md text-sm" style={{color: "var(--os-text-muted)"}}>
              Real Estate. Loans. Connectors. One command centre for lead ingestion, SLA enforcement, pipeline movement and revenue tracking.
            </p>
          </div>
          <div className="text-xs" style={{color: "var(--os-text-dim)"}}>© {new Date().getFullYear()} Our First Realty LLP</div>
        </div>

        {/* Right form panel */}
        <div className="flex items-center justify-center p-8" style={{background: "var(--os-bg)"}}>
          <form onSubmit={submit} data-testid="os-login-form" className="w-full max-w-sm">
            <div className="mb-6">
              <div className="inline-flex items-center gap-2 os-badge os-badge-blue mb-4">
                <LogIn className="w-3 h-3" /> Team sign-in
              </div>
              <h2 className="font-bold text-3xl tracking-tight" style={{color: "var(--os-text)"}}>Sign in</h2>
              <p className="text-sm mt-1" style={{color: "var(--os-text-muted)"}}>Admin, Manager, Agent or Telecaller access.</p>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs" style={{color: "var(--os-text-muted)"}}>Email</label>
                <input data-testid="os-login-email" className="os-input mt-1" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="agent@ourfirstrealty.com" required />
              </div>
              <div>
                <label className="text-xs" style={{color: "var(--os-text-muted)"}}>Password</label>
                <input data-testid="os-login-password" className="os-input mt-1" type="password" value={password} onChange={e => setPassword(e.target.value)} required />
              </div>
              <button type="submit" disabled={loading} data-testid="os-login-submit" className="os-btn w-full justify-center h-10">
                {loading ? "Signing in…" : "Sign in"}
              </button>
            </div>
            <div className="mt-6 text-[11px]" style={{color: "var(--os-text-dim)"}}>
              Test users:
              <div className="mt-1 os-mono" style={{color: "var(--os-text-muted)"}}>
                admin@ourfirstrealty.com · manager@ourfirstrealty.com · agent1@ourfirstrealty.com
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
